import React, { useMemo /*, useState*/ } from 'react'; // useState — restore for Change Art when artSeed persists

import {
	FirstStep,
	FirstStepFormSchema,
	firstStepValidationSchema,
} from '@/components/Layout/components/AddCustomSong/AddCustomSongRoot/components/FirstStep';
import { SettingsListItems } from '@/components/shared/SettingCard';
import { SongThemeChip } from '@/components/shared/SongThemeChip';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { SingleActionLayout } from '@/components/shared/SingleActionLayout/SingleActionLayout';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useFormik } from 'formik';

import {
	ESongModalDisplayState,
	IPropsSongModalDisplay,
} from '../SongModalPanel';
import styles from './SongInformation.module.scss';

export const SongInformation: React.FC<IPropsSongModalDisplay> = ({
	pushDisplayState,
	openedSong,
	popDisplayState,
	setOpenedSong,
	updateTeamSong,
}) => {
	// const [artIndex, setArtIndex] = useState(0); // Change Art — pending artSeed persistence on backend (CTO)
	// const albumGradient = generateAlbumGradient(artIndex === 0 ? openedSong.name : openedSong.name + '-' + artIndex); // Change Art — restore when artSeed persists
	const albumGradient = generateAlbumGradient(openedSong.name);

	const fireSaveSongInformationEvent = useAnalyticsEvent(
		'View_Song_Save_Song_Information',
	);

	const initialValues: FirstStepFormSchema = useMemo(
		() => ({
			name: openedSong.name || '',
			artist: openedSong.artist || '',
			ccli: openedSong.ccli || undefined,
		}),
		[openedSong],
	);

	const formikFirstStep = useFormik({
		initialValues,
		validationSchema: firstStepValidationSchema,
		onSubmit: (values) => {
			updateTeamSong({
				...values,
				ccli:
					typeof values.ccli !== 'undefined'
						? parseInt(values.ccli as unknown as string)
						: null,
			});
			fireSaveSongInformationEvent();
			popDisplayState();
		},
	});

	const additionalItems: SettingsListItems = [
		{
			type: 'title',
			title: 'Song info',
		},
		{
			type: 'setting',
			title: 'Song Lyrics',
			subtitle: (() => {
				const firstLine = openedSong.lyrics
					?.split('\n')
					.find((line) => line.trim().length > 0);
				return firstLine ? `${firstLine}…` : 'No lyrics added';
			})(),
			onClick: () => {
				pushDisplayState(ESongModalDisplayState.EDIT_LYRICS);
			},
		},
		{ type: 'title', title: 'Tags' },
		{
			type: 'setting',
			title: 'Energy',
			trailingComponent: openedSong.energyTags?.length ? (
				<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
					{openedSong.energyTags.map((tag) => <SongThemeChip key={tag} themeTag={tag} />)}
				</div>
			) : undefined,
			onClick: () => pushDisplayState(ESongModalDisplayState.ENERGY_TAG),
		},
		{
			type: 'setting',
			title: 'Song Type',
			trailingComponent: openedSong.songTypeTags?.length ? (
				<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
					{openedSong.songTypeTags.map((tag) => <SongThemeChip key={tag} themeTag={tag} />)}
				</div>
			) : undefined,
			onClick: () => pushDisplayState(ESongModalDisplayState.SONG_TYPE_TAG),
		},
		{
			type: 'title',
			title: 'Listen',
		},
		{
			type: 'setting',
			title: 'Spotify URL',
			onClick: () => {
				pushDisplayState(ESongModalDisplayState.SPOTIFY_URL);
			},
			trailingText: openedSong.spotifyId,
		},
		{
			type: 'setting',
			title: 'YouTube URL',
			onClick: () => {
				pushDisplayState(ESongModalDisplayState.YOUTUBE_URL);
			},
			trailingText: openedSong.youtubeId,
		},
		{
			type: 'setting',
			title: 'Apple Music URL',
			onClick: () => {
				pushDisplayState(ESongModalDisplayState.APPLE_MUSIC_URL);
			},
			trailingText: openedSong.appleMusicId,
		},
		{
			type: 'setting',
			title: 'Recorded key',
			onClick: () => {
				pushDisplayState(ESongModalDisplayState.RECORDED_KEY);
			},
			trailingComponent: openedSong.recordedKey ? (
				<SongThemeChip themeTag={openedSong.recordedKey} />
			) : undefined,
		},
	];

	return (
		<SingleActionLayout
			actionLabel="Save custom song"
			actionDisabled={!formikFirstStep.isValid}
			actionDivider
			actionClassName={styles['next-button']}
			onActionClick={formikFirstStep.handleSubmit}
		>
			<FirstStep
				formik={formikFirstStep}
				additionalItems={additionalItems}
				albumGradient={albumGradient}
				// onChangeArt={() => setArtIndex((i) => i + 1)} // Change Art — pending artSeed persistence on backend (CTO)
				// setTeamSong={setDraftTeamSong}
			/>
		</SingleActionLayout>
	);
};
